import React, { useState, useEffect } from "react";
import { Container, Typography } from "@mui/material";
import styles from "./ticketsTabBarStyles.js";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import TicketsTableRegistrado from "../TicketsTableRegistrado/index.js";
import TicketsTableProcesando from "../TicketsTableProcesando/index.js";
import TicketsTableTerminado from "../TicketsTableTerminado/index.js";
import SearchBar from "../../SharedComponents/SearchBar/index.js";

// Función para mapear el valor del tab al status del API
const getStatusFromTab = (tabValue) => {
  const statusMap = {
    "1": "registrado",
    "2": "procesando", // El API usa "procesando" no "procesado"
    "3": "terminado",
  };
  return statusMap[tabValue] || "registrado";
};

// Función para mapear el status a el valor del tab
const getTabFromStatus = (status) => {
  const tabMap = {
    "registrado": "1",
    "procesando": "2",
    "procesado": "2", // También aceptar "procesado" por si acaso
    "terminado": "3",
  };
  return tabMap[status] || "1";
};

const TicketsTabBar = ({ tickets = [], isLoading, fetchTickets, search, setSearch, currentStatus, setCurrentStatus, counters = {} }) => {
  const [value, setValue] = useState(getTabFromStatus(currentStatus));
  
  // Estados independientes para cada tab
  const [ticketsRegistrado, setTicketsRegistrado] = useState([]);
  const [ticketsProcesando, setTicketsProcesando] = useState([]);
  const [ticketsTerminado, setTicketsTerminado] = useState([]);
  
  const [isLoadingRegistrado, setIsLoadingRegistrado] = useState(false);
  const [isLoadingProcesando, setIsLoadingProcesando] = useState(false);
  const [isLoadingTerminado, setIsLoadingTerminado] = useState(false);

  // Sincronizar el valor del tab con el status actual
  useEffect(() => {
    setValue(getTabFromStatus(currentStatus));
  }, [currentStatus]);

  // Actualizar los tickets del tab actual cuando cambian los tickets del padre
  useEffect(() => {
    const statusMap = {
      "registrado": {
        setTickets: setTicketsRegistrado,
        setIsLoading: setIsLoadingRegistrado,
      },
      "procesando": {
        setTickets: setTicketsProcesando,
        setIsLoading: setIsLoadingProcesando,
      },
      "terminado": {
        setTickets: setTicketsTerminado,
        setIsLoading: setIsLoadingTerminado,
      },
    };
    
    const tabState = statusMap[currentStatus];
    if (tabState) {
      tabState.setTickets(tickets);
      tabState.setIsLoading(isLoading);
    }
  }, [tickets, currentStatus, isLoading]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    const newStatus = getStatusFromTab(newValue);
    setCurrentStatus(newStatus);
    // Llamar al API con el nuevo status
    if (fetchTickets) {
      fetchTickets(newStatus);
    }
  };

  // Funciones de fetch independientes para cada tab
  const fetchTicketsRegistrado = () => {
    if (fetchTickets) {
      fetchTickets("registrado");
    }
  };

  const fetchTicketsProcesando = () => {
    if (fetchTickets) {
      fetchTickets("procesando");
    }
  };

  const fetchTicketsTerminado = () => {
    if (fetchTickets) {
      fetchTickets("terminado");
    }
  };

  return (
    <Container style={styles.primaryContainer}>
      <TabContext value={value}>
        <Container style={styles.mainContainer}>
          <Box sx={styles.tabContainer}>
            <TabList
              onChange={handleChange}
              aria-label="tabs de tickets"
              variant="fullWidth"
            >
              <Tab 
                label="Registrado" 
                value="1"
                icon={
                  <Typography style={styles.badge}>
                    {counters.registrado || 0}
                  </Typography>
                }
                iconPosition="end"
              />
              <Tab 
                label="Procesando" 
                value="2"
                icon={
                  <Typography style={styles.badge}>
                    {counters.procesando || 0}
                  </Typography>
                }
                iconPosition="end"
              />
              <Tab 
                label="Terminado" 
                value="3"
                icon={
                  <Typography style={styles.badge}>
                    {counters.terminado || 0}
                  </Typography>
                }
                iconPosition="end"
              />
            </TabList>
          </Box>
        </Container>

        <SearchBar setSearch={setSearch} />

        <Container
          style={styles.mainContainer}
          sx={{ padding: "0 !important" }}
        >
          <Box sx={{ width: "100%" }}>
            <TabPanel value="1">
              <TicketsTableRegistrado
                tickets={ticketsRegistrado}
                search={search}
                fetchTickets={fetchTicketsRegistrado}
                isLoading={isLoadingRegistrado}
                currentStatus="registrado"
              />
            </TabPanel>
            <TabPanel value="2">
              <TicketsTableProcesando
                tickets={ticketsProcesando}
                search={search}
                fetchTickets={fetchTicketsProcesando}
                isLoading={isLoadingProcesando}
                currentStatus="procesando"
              />
            </TabPanel>
            <TabPanel value="3">
              <TicketsTableTerminado
                tickets={ticketsTerminado}
                search={search}
                fetchTickets={fetchTicketsTerminado}
                isLoading={isLoadingTerminado}
                currentStatus="terminado"
              />
            </TabPanel>
          </Box>
        </Container>
      </TabContext>
    </Container>
  );
};

TicketsTabBar.propTypes = {
  tickets: PropTypes.array,
  isLoading: PropTypes.bool,
  fetchTickets: PropTypes.func,
  search: PropTypes.string,
  currentStatus: PropTypes.string,
  setCurrentStatus: PropTypes.func,
  counters: PropTypes.object,
};

export default TicketsTabBar;

